import React, { useState, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import SignatureSidebar from "./utils/SignatureSidebar";
import PDFDropZone from "./utils/PDFDropZone";
import { FieldData } from "./utils/DraggableResizableField";
import { useNotification } from "../../../context/NotificationContext";

interface PrepareSignatureProps {
  pdfUrl: string;
  onClose: () => void;
  onReadyForSignature: (fields: FieldData[], dimensions: { width: number; height: number }) => Promise<void>;
  readyButtonLabel?: string;
}

/**
 * PrepareSignature Component
 *
 * Used to place signature fields on a PDF before preparing it for signature.
 * User drags and drops Signature, Text, Date, and Tampon fields onto the PDF.
 *
 * Usage:
 * ```tsx
 * // Option 1: Prepare via backend
 * <PrepareSignature
 *   pdfUrl={pdfFileUrl}
 *   onClose={() => setOpen(false)}
 *   readyButtonLabel="Prêt pour signature"
 *   onReadyForSignature={async (fields, dimensions) => {
 *     const response = await prepareSignatureAPI(fields, dimensions);
 *     setPreparedPdfUrl(response.pdf_url);
 *   }}
 * />
 *
 * // Option 2: Open signature modal directly
 * <PrepareSignature
 *   pdfUrl={pdfFileUrl}
 *   onClose={() => setOpen(false)}
 *   readyButtonLabel="Signer maintenant"
 *   onReadyForSignature={async (fields, dimensions) => {
 *     setSignatureFields(fields);
 *     setSignatureDimensions(dimensions);
 *     openFillSignatureModal();
 *   }}
 * />
 * ```
 */
const PrepareSignature: React.FC<PrepareSignatureProps> = ({
  pdfUrl,
  onClose,
  onReadyForSignature,
  readyButtonLabel = "Prêt pour signature",
}) => {
  const { showNotification } = useNotification();
  const [signFields, setSignFields] = useState<FieldData[]>([]);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 1190.56, height: 1683.78 });
  const [loadingData, setLoadingData] = useState(false);
  const fieldIdCounter = useRef(0);

  const addField = (
    type: string,
    name: string | null = null,
    top: number = 100,
    left: number = 100,
    width: number = 500,
    height: number = 120,
    page: number = 1,
  ) => {
    console.log("pdf dimensions in addField:", pdfDimensions);
    const newField: FieldData = {
      id: fieldIdCounter.current,
      type: type,
      name: name || type,
      width: width,
      height: height,
      top: top,
      left: left,
      page: page,
    };
    fieldIdCounter.current += 1;
    setSignFields((prevFields) => [...prevFields, newField]);
  };

  const handleUpdateFields = (
    id: number,
    type: string,
    name: string | null = null,
    top: number = 100,
    left: number = 100,
    width: number = 150,
    height: number = 80,
    page: number = 1,
  ) => {
    const updateName = name || type;
    console.log("x coord ", left)
    setSignFields((prevFields) =>
      prevFields.map((field) =>
        field.id === id
          ? {
              ...field,
              type: type,
              name: updateName,
              width: width,
              height: height,
              top: top,
              left: left,
              page: page,
            }
          : field
      )
    );
  };

  const handleFieldDelete = (id: number) => {
    setSignFields((prevFields) => prevFields.filter((field) => field.id !== id));
  };

  const handleFieldResize = (id: number, width: number, height: number) => {
    setSignFields((prevFields) =>
      prevFields.map((field) => (field.id === id ? { ...field, width, height } : field))
    );
  };

  const handleReadyForSignature = async () => {
    if (signFields.length === 0) {
      showNotification({ message: "Veuillez ajouter au moins un champ avant de continuer", type: "error" });
      return;
    }

    setLoadingData(true);
    try {
      if (onReadyForSignature) {
        await onReadyForSignature(signFields, pdfDimensions);
      }
    } catch (error) {
      console.error("Error processing signature preparation:", error);
      showNotification({ message: "Échec de la préparation", type: "error" });
    } finally {
      setLoadingData(false);
    }
  };

  return (
    <div className="h-full w-full rounded-lg bg-white overflow-hidden">
      <DndProvider backend={HTML5Backend}>
        <div className="flex h-full w-full overflow-hidden">
          <SignatureSidebar
            editorMode="sign"
            handleEditorMode={() => {}}
            onSign={handleReadyForSignature}
            onEdit={() => {}}
            loadingData={loadingData}
            onClose={onClose}
            fields={signFields}
            handleRemoveTextField={() => {}}
            setIsModalOpen={() => {}}
            customTextFields={[]}
            signButtonLabel={readyButtonLabel}
          />

          <PDFDropZone
            pdfUrl={pdfUrl}
            editFields={[]}
            signFields={signFields}
            addField={addField}
            editorMode="sign"
            handleFieldDelete={(id) => handleFieldDelete(id)}
            handleFieldResize={(id, w, h) => handleFieldResize(id, w, h)}
            handleUpdateFields={handleUpdateFields}
            setPdfDimensions={setPdfDimensions}
            pdfDimensions={pdfDimensions}
          />
        </div>
      </DndProvider>
    </div>
  );
};

export default PrepareSignature;
